import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TypeFarming = ({
  label = "TYPE OF FARMING",
  value,
  onChange,
  hintText = "Select your farming type",
  required = false,
  errorMessage = "This field is required",
  isPreview = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);
  const [isTouched, setIsTouched] = useState(false);

  // Define farming types with the new structure
  const farmingTypes = [
    {
      "heading": "CROPS",
      "items": [
        "African nightshade",
        "Aloe vera",
        "Avocados",
        "Apple",
        "Bananas",
        "Baobab",
        "Beans",
        "Beetroot",
        "Black nightshade",
        "Cabbage",
        "Capsicum (Bell Pepper)",
        "Cassava",
        "Castor Beans",
        "Chili pepper",
        "Cocoa",
        "Coffee",
        "Cotton",
        "Cowpeas",
        "Dragon Fruit",
        "Eggplant",
        "Garlic",
        "Ginger",
        "Groundnuts (peanuts)",
        "Guavas",
        "Hemp (Cannabis)",
        "Jatropha",
        "Jute mallow",
        "Kale",
        "Lentils",
        "Lemon",
        "Lime",
        "Maize (corn)",
        "Macadamia",
        "Millet",
        "Mango",
        "Moringa",
        "Okra",
        "Onion",
        "Orange",
        "Palm oil",
        "Pineapples",
        "Plantain",
        "Potatoes (Irish)",
        "Potatoes (Yellow Flesh)",
        "Pumpkin",
        "Rice",
        "Sorghum",
        "Soybeans",
        "Spider plant",
        "Spinach",
        "Spring Onions",
        "Strawberry",
        "Sugarcane",
        "Sunflower",
        "Sweet potatoes",
        "Tangarine",
        "Tea",
        "Tomatoes",
        "Water melon",
        "Vanilla",
        "Yam"
      ]
    },
    {
      "heading": "LIVESTOCK",
      "items": [
        "Bees",
        "Cattle - Dairy",
        "Cattle - Zebu",
        "Cattle - Ankole",
        "Cattle - Nguni",
        "Cattle - Afrikaner",
        "Cattle - N'Dama",
        "Camels",
        "Chicken",
        "Donkeys",
        "Ducks",
        "Gunea Fowls",
        "Goats - Red Sokoto",
        "Goats - Boer Giant",
        "Goats - Sahelian",
        "Goats - East African",
        "Horses",
        "Pigs",
        "Rabbit",
        "Sheep - West African Dwarf",
        "Sheep - Dorper",
        "Sheep - Fat Tailed",
        "Sheep - Blackhead Persian",
        "Turkey"
      ]
    },
    {
      "heading": "FISH",
      "items": [
        "Catfish",
        "Common Carp",
        "Trout",
        "Tilapia"
      ]
    },
    {
      "heading": "FORESTRY",
      "items": [
        "Forest"
      ]
    }
  ];

  // Validation function
  const validate = () => {
    if (!required) return true;
    return value !== null && value !== undefined && value !== "";
  };

  const isValid = validate();
  const showError = required && isTouched && !isValid;

  // Scroll handling functions
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, farmingTypes]);

  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio = (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const UnselectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[24px] md:h-[24px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" />
    </svg>
  );
  const SelectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[24px] md:h-[24px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#FFBD26" />
    </svg>
  );

  const handleButtonClick = () => {
    setIsTouched(true);
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isTouched) {
      validate();
    }
  }, [value, isTouched]);

  const handleItemClick = (item) => {
    setIsTouched(true);
    onChange("TYPE OF FARMING", item);
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (!value) return "-";
    return value;
  };

  const capitalizeWords = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const renderGroupedItems = () => {
    if (!farmingTypes || !Array.isArray(farmingTypes)) return null;

    return farmingTypes.map((section, sectionIndex) => (
      <React.Fragment key={sectionIndex}>
        <li className="text-[4px] md:text-[18px] font-bold underline text-white px-1 md:px-3 py-1 sticky top-0 z-10">
          {section.heading}
        </li>
        {section.items.map((item, itemIndex) => {
          const isSelected = value === item;

          return (
            <li
              key={`${sectionIndex}-${itemIndex}`}
              onMouseEnter={() => setHoveredItem(item)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleItemClick(item);
              }}
              className="flex justify-between items-center px-[3px] md:px-3 py-1 md:py-1 rounded-md  cursor-pointer relative"
            >
              <span className="text-white text-[4px] md:text-[16px] flex-1 mr-2 whitespace-normal break-words">
                {item}
              </span>
              <div className="flex items-center">
                {isSelected ? <SelectedIcon /> : <UnselectedIcon />}
              </div>
            </li>
          );
        })}
      </React.Fragment>
    ));
  };

  if (isPreview) {
    return (
      <div className="flex flex-col">
        <label className="md:text-[20px] font-bold text-[5px] text-black mb-1 capitalize truncate">
          {label}
        </label>
        <div className="flex relative z-20 w-full">
          <button
            className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] truncate`}
          >
            <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full`}>
              {value || "-"}
            </span>
          </button>
          <div
            className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] bg-[#152C24] flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
          >
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full md:mt-1 mt-1 relative "
      style={{ position: "relative", zIndex: isOpen ? 100 : 1 }}
      ref={dropdownRef}
    >
      <label className="md:text-[20px] font-bold text-[5px] text-black mb-1 capitalize truncate">
        {label}
      </label>
      {showError && (
        <span className="text-red-500 text-[3px] md:text-[12px]">
          {errorMessage}
        </span>
      )}
      <div
        className="flex relative z-20 w-full"
        onMouseEnter={handleMouseEnter}
        onClick={handleButtonClick}
      >
        <button
          className={`relative xl:w-[250px] lg:w-[140px] md:w-[100px] w-[67px] h-[15px] md:h-[53px] bg-[#152C24] text-white flex justify-between px-2 items-center text-[4px] md:text-[17px]  rounded-l-[4px] truncate`}
        >
          <span className={`whitespace-normal break-words font-bold leading-[0.90] text-left capitalize w-full ${
            getDisplayValue() === "-" ? "text-gray-400" : ""
          }`}>
            {capitalizeWords(getDisplayValue())}
          </span>
        </button>
        <div
          className={`w-[20px] md:w-[50px] h-[15px] md:h-[53px] bg-[#FDBB26] flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
        >
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
            alt="expand"
            className="w-2 h-2 md:w-5 md:h-[24px]"
          />
        </div>
      </div>
      <p className={`text-[4px] md:text-[14px] mt-[2px] truncate ${
        showError ? "text-red-500" : "text-black"
      }`}>
        {showError ? errorMessage : hintText}
      </p>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-1 left-0 w-full md:w-[290px] border border-[#979797]/80 bg-[#152C24] rounded shadow-lg p-[0px] md:p-2 md:max-h-[700px] flex flex-col"
            style={{
              position: "absolute",
              zIndex: 1000,
              backgroundColor: "#152C24",
            }}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <li className="flex items-center justify-end text-[4px]   md:text-[14px] font-semibold md:mb-2">
            
              <img
                src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1758380484/Export_1_yad3hi.png"
                alt="icon"
                className="md:w-[30px] md:h-[30px] h-[8px] w-[8px] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              />
            </li>

         

            <div className="custom-scroll-area">
              <div
                className="custom-scroll-content"
                ref={contentRef}
                onScroll={handleScroll}
              >
                <ul className="md:px-2 ">
                  {renderGroupedItems()}
                </ul>
              </div>
              <div className="custom-scroll-track" onClick={handleTrackClick}>
                <div
                  ref={thumbRef}
                  className="custom-scroll-thumb"
                  style={{
                    height: `${thumbHeight}px`,
                    top: `${scrollTop}px`,
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                  onMouseDown={handleThumbMouseDown}
                ></div>
              </div>
            </div>

         
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 350px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 1px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
          .custom-scroll-content {
            flex: 1;
            overflow-y: scroll;
            scrollbar-width: none;
            -ms-overflow-style: none;
            padding-right: 0px;
          }
        }
      `}</style>
    </div>
  );
};

export default TypeFarming;